import { setSearchText } from '@propertypal/shared/src/reducers/search';
import axios, { API_URL } from '@propertypal/shared/src/services/axios';
import { AgentResult, AgentResults, Reason } from '@propertypal/shared/src/types/agents';
import platform from '@propertypal/shared/src/utils/platform';
import WhiteModal from '@propertypal/web-ui/src/modal/WhiteModal';
import { Text } from '@propertypal/web-ui/src/typography';
import Head from 'next/head';
import { useRouter } from 'next/router';
import React, { FunctionComponent, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import AgentEmailForm from '../../components/find-an-agent/AgentEmailForm';
import AgentsResults from '../../components/find-an-agent/AgentsResults';
import generateMetaTags from '../../components/layout/MetaTags';
import IMAGE_LOCATOR from '../../constants/imageLocator';
import cachedRequest from '../../services/cachedRequest';
import { requireAuthToken } from '../../utils/auth';
import stitchRequestUrl from '../../utils/stitchRequestUrl';

interface Props {
  data: AgentResults;
  reasons: Reason[];
}

export const getServerSideProps = requireAuthToken(async (ctx, store) => {
  const [url] = stitchRequestUrl(ctx.query);

  const result = await axios<AgentResults>(
    {
      method: 'GET',
      url: `${API_URL}/agent-results?url=${encodeURIComponent(url)}&imageLocator=${encodeURIComponent(IMAGE_LOCATOR)}`,
    },
    undefined,
    ctx,
  );

  const reasons = await cachedRequest('/account/reasons', ctx, 72);

  store.dispatch(setSearchText(result.data.queryText || ''));

  if (result.data.url.replace(/%60/g, '') !== url.replace(/%60/g, '')) {
    ctx.res.setHeader('Cache-control', 'no-cache');

    return {
      redirect: {
        destination: result.data.url,
        permanent: true,
      },
      props: {
        data: result.data,
        reasons,
      },
    };
  }

  return {
    props: {
      data: result.data,
      reasons,
    },
  };
});

const EstateAgents: FunctionComponent<Props> = (props) => {
  const [emailAgent, setEmailAgent] = useState<AgentResult>();
  const [data, setData] = useState<AgentResults>(props.data);
  const [loading, setLoading] = useState<boolean>(false);
  const dispatch = useDispatch();
  const router = useRouter();
  const title = `${data.description.complete} - ${platform.title}`;

  const onSearch = async (params: string) => {
    setLoading(true);

    try {
      const result = await axios<AgentResults>(
        {
          method: 'GET',
          url: `${API_URL}/agent-results?${params}&imageLocator=${encodeURIComponent(IMAGE_LOCATOR)}`,
        },
        dispatch,
      );

      setData(result.data);
      router.push(result.data.url, undefined, {
        shallow: true,
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchViaUrl = async () => {
    setLoading(true);

    try {
      const result = await axios<AgentResults>(
        {
          method: 'GET',
          url: `${API_URL}/agent-results?url=${encodeURIComponent(router.asPath)}&imageLocator=${encodeURIComponent(
            IMAGE_LOCATOR,
          )}`,
        },
        dispatch,
      );
      dispatch(setSearchText(result.data.queryText || ''));

      setData(result.data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!loading && data.url !== router.asPath) {
      fetchViaUrl();
    }
  }, [router.asPath]);

  return (
    <>
      <Head>
        {generateMetaTags({
          title,
          description: props.data.description.metaDescription,
          url: props.data.url,
        })}
      </Head>

      <AgentsResults
        results={data}
        onEmailClick={setEmailAgent}
        reasons={props.reasons}
        onSearch={onSearch}
        loading={loading}
      />

      <WhiteModal show={!!emailAgent} onClose={() => setEmailAgent(undefined)} title="Email the agent">
        <img src={emailAgent?.account.logo?.standard} style={{ maxHeight: 60, marginBottom: 30 }} alt="agent logo" />

        <Text align="center" fontWeight="bold">
          Request a valuation or make a general enquiry
        </Text>

        <AgentEmailForm
          // @ts-ignore
          agents={[emailAgent?.account]}
          loggedInUser={data.loggedInUser}
          reasons={props.reasons}
          onSubmit={() => setEmailAgent(undefined)}
        />
      </WhiteModal>
    </>
  );
};

export default EstateAgents;
